import { type ReactNode, useCallback, useId, useState } from 'react';

import { cn } from '@shared/lib/cn';
import { Button } from '@shared/ui/button';
import { DialogParts, ModalLayer } from '@shared/ui/dialog';

import {
  ALERT_DIALOG_CANCEL_CLASS,
  ALERT_DIALOG_CONFIRM,
  ALERT_DIALOG_PANEL_CLASS,
  type AlertDialogStatus,
  type AlertDialogTone,
} from './alert-dialog.constant';
import { ALERT_DIALOG_TEXT } from './alert-dialog.text';

export interface AlertDialogProps {
  /** 제어 prop. 비제어는 `defaultOpen` */
  open?: boolean;
  defaultOpen?: boolean;
  /** 취소 · Esc 로 닫힐 때 `false` */
  onOpenChange?: (open: boolean) => void;
  /** 묻는 말(제목) */
  title: ReactNode;
  /** 제목 아래 설명(`aria-describedby`) */
  description?: ReactNode;
  /** Figma `tone`. danger = 되돌릴 수 없는 삭제(빨간 확인 버튼). 기본 default */
  tone?: AlertDialogTone;
  /** Figma `status`. loading = 흐리게 + Spinner, 누를 수 없다 */
  status?: AlertDialogStatus;
  confirmLabel?: ReactNode;
  cancelLabel?: ReactNode;
  /** 확인. 닫는 것은 부르는 쪽이 정한다 */
  onConfirm?: () => void;
  /** 취소. 부른 뒤 닫는다 */
  onCancel?: () => void;
  /** portal 대신 제자리에 그린다(fe-rules 3절) */
  inline?: boolean;
  className?: string;
}

/**
 * 되돌릴 수 없는 동작 전에 확인을 받는 창(`window.confirm` 자리). Dialog 의 안쪽(`DialogParts` headerless —
 * 머리 줄 없이 제목은 본문 첫 줄 16 보통 굵기, 본문 위아래 20)을 쓴다.
 * `role="alertdialog"`, 닫기(×) 없음. 배경을 누르면 취소와 같다(불러오는 중에는 닫지 않는다 — 2026-10-07 사용자).
 * 명령형으로는 `openAlertDialog({ title, … })` → `Promise<boolean>`.
 */
export function AlertDialog({
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  title,
  description,
  tone = 'default',
  status = 'default',
  confirmLabel = ALERT_DIALOG_TEXT.confirm[tone],
  cancelLabel = ALERT_DIALOG_TEXT.cancel,
  onConfirm,
  onCancel,
  inline = false,
  className,
}: AlertDialogProps) {
  const id = useId();
  const titleId = `${id}-title`;
  const bodyId = `${id}-body`;
  const [openState, setOpenState] = useState(defaultOpen);
  const open = openProp ?? openState;
  const loading = status === 'loading';
  const confirm = ALERT_DIALOG_CONFIRM[tone];

  const setOpen = useCallback(
    (next: boolean) => {
      if (openProp === undefined) setOpenState(next);
      onOpenChange?.(next);
    },
    [openProp, onOpenChange],
  );
  const cancel = useCallback(() => {
    onCancel?.();
    setOpen(false);
  }, [onCancel, setOpen]);

  return (
    <ModalLayer
      open={open}
      onDismiss={cancel}
      placement="center"
      layer="modal"
      role="alertdialog"
      labelledBy={titleId}
      describedBy={description !== undefined ? bodyId : undefined}
      closeOnBackdrop={!loading}
      closeOnEscape={!loading}
      busy={loading}
      inline={inline}
      data-tone={tone}
      data-status={status}
      className={cn(ALERT_DIALOG_PANEL_CLASS, className)}
    >
      <DialogParts
        headerless
        titleId={titleId}
        bodyId={bodyId}
        title={title}
        body={description}
        status={status}
        footer={
          <>
            <Button
              variant="neutral"
              size="sm"
              className={ALERT_DIALOG_CANCEL_CLASS}
              onClick={cancel}
            >
              {cancelLabel}
            </Button>
            <Button
              variant={confirm.variant}
              size={confirm.size}
              className={confirm.className}
              onClick={onConfirm}
            >
              {confirmLabel}
            </Button>
          </>
        }
      />
    </ModalLayer>
  );
}
